import React from 'react';
import { motion } from 'framer-motion';
import { ScrollReveal } from './ScrollReveal';
import { useClient } from './ClientContext';
const steps = [{
  id: '01',
  title: 'Discovery & Positioning',
  description: 'We run a focused discovery workshop to understand the organisation, its audiences and the current brand challenges. From this we define the positioning, messaging and strategic direction for the rebrand.',
  included: ['Discovery deep dive workshop', 'Audience and referral pathway mapping', 'Brand positioning framework', 'Messaging clarity for key audiences']
}, {
  id: '02',
  title: 'Brand Identity Development',
  description: 'We develop the visual identity system including logo, typography, colour palette and visual language. The identity is designed to communicate expertise and build trust with solicitors, case managers and referral partners.',
  included: ['Logo system development', 'Typography and colour palette', 'Visual language and graphic elements', 'Exploration of brand structure and naming']
}, {
  id: '03',
  title: 'Guidelines & Assets',
  description: 'We produce comprehensive brand guidelines and design the marketing assets the team needs to present the organisation consistently and professionally.',
  included: ['Brand guidelines document', 'Business cards and email signatures', 'Roll up banners and conference brochures', 'Social media templates']
}, {
  id: '04',
  title: 'Rollout & Handover',
  description: 'We hand over all brand assets with clear guidance so the team can apply the new identity across communications, marketing materials and referral partner touchpoints with confidence.',
  included: ['Complete asset handover', 'Application guidance', 'Team walkthrough', 'Clean file structure']
}];
export function ApproachTimeline() {
  const {
    companyName
  } = useClient();
  return <section id="approach" className="py-20 md:py-28 px-4 md:px-6 border-b border-white/[0.06]" data-id="element-17">
      <div className="max-w-7xl mx-auto" data-id="element-18">
        <ScrollReveal data-id="element-19">
          <div className="mb-12 md:mb-16 flex flex-col gap-4" data-id="element-20">
            <h2 className="text-xs md:text-sm font-bold tracking-[0.2em] text-accent uppercase" data-id="element-21">
              {companyName} — Approach
            </h2>
            <p className="text-xl md:text-3xl text-white max-w-2xl font-light leading-relaxed" data-id="element-22">
              A structured process to move from a brand that reflects the past
              to one that clearly represents the expertise and direction of the
              organisation today.
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-white/[0.06]" data-id="element-23">
          {steps.map((step, index) => <ScrollReveal key={step.id} delay={index * 0.1} data-id="element-24">
              <motion.div className="flex flex-col h-full border-r border-b border-white/[0.06] p-6 md:p-8 hover:bg-white/[0.02] transition-colors duration-500" initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.12,
            ease: [0.25, 0.1, 0.25, 1]
          }} data-id="element-25">
                <div className="flex flex-col h-full" data-id="element-26">
                  <div className="mb-6 md:mb-8" data-id="element-27">
                    <span className="block text-xs md:text-sm font-mono text-zinc-500 mb-3 md:mb-4 tracking-widest" data-id="element-28">
                      [{step.id}]
                    </span>
                    <h3 className="text-xl md:text-2xl font-bold text-white uppercase tracking-tight" data-id="element-29">
                      {step.title}
                    </h3>
                  </div>

                  <p className="text-zinc-400 text-sm md:text-base leading-relaxed flex-1 mb-6 md:mb-8" data-id="element-30">
                    {step.description}
                  </p>

                  <div className="mt-auto pt-5 md:pt-6 border-t border-white/[0.06]" data-id="element-31">
                    <h4 className="text-white text-[11px] font-bold tracking-widest uppercase mb-4 opacity-50" data-id="element-32">
                      Included
                    </h4>
                    <ul className="space-y-3" data-id="element-33">
                      {step.included.map((item, i) => <li key={i} className="flex items-start text-xs md:text-sm text-zinc-500 font-mono" data-id="element-34">
                          <span className="mr-3 text-accent" data-id="element-35">+</span>
                          {item}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </motion.div>
            </ScrollReveal>)}
        </div>
      </div>
    </section>;
}